import {
  Directive,
  ElementRef,
  Inject,
  Optional,
  Renderer2,
  SkipSelf,
} from '@angular/core';
import {
  COMPOSITION_BUFFER_MODE,
  DefaultValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import { isDefined } from '@freelancer/utils';

/**
 * The following directive purpose to override the DefaultValueAccessor
 * implementiation by supporting to set-up the cursor position after
 * the super.writeValue(value); was called but befure the valueChanges
 * would be fired.
 *
 * Related links:
 * - The problem: https://www.mutuallyhuman.com/blog/the-curious-case-of-cursor-jumping/
 * - Solution: https://stackoverflow.com/questions/43492421/angular-2-cursor-jump-at-the-end-of-textbox-when-changing-data
 * - Documention: https://angular.io/api/forms/DefaultValueAccessor
 * - Source: https://github.com/angular/angular/blob/master/packages/forms/src/directives/default_value_accessor.ts
 */
@Directive({
  selector: '[flControlValueAccessor]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ControlValueAccessorDirective,
      multi: true,
    },
  ],
})
export class ControlValueAccessorDirective extends DefaultValueAccessor {
  private offset: number | undefined;

  constructor(
    private elementRef: ElementRef,
    private timeUtils: TimeUtils,
    @SkipSelf()
    renderer: Renderer2,
    @Optional()
    @Inject(COMPOSITION_BUFFER_MODE)
    compositionMode: boolean,
  ) {
    super(renderer, elementRef, compositionMode);
  }

  writeValue(value: any): void {
    super.writeValue(value);

    // Wrap setting the focus of the native element in a timeout to ensure that the element is rendered before
    // attempting to focus.
    this.timeUtils.setTimeout(() => {
      if (!isDefined(this.offset)) {
        return;
      }

      // Implementation to set-up the cursor position.
      const { nativeElement } = this.elementRef;

      nativeElement.focus();
      nativeElement.setSelectionRange(this.offset, this.offset);
    });
  }

  setCursorPosition(offset?: number) {
    this.offset = offset;
  }

  getCursorSelectionPosition(): {
    selectionStart: number;
    selectionEnd: number;
  } {
    const { nativeElement } = this.elementRef;

    return {
      selectionStart: nativeElement.selectionStart,
      selectionEnd: nativeElement.selectionEnd,
    };
  }
}
